/**
 * Service-provider-specific availability helper.
 *
 * This subclass extends AvailabilityHelper with the "base availability"
 * (contract-level AVAILABLE block) concept. All generic availability logic
 * lives in @/helper/availability/availability-helper.ts.
 */

import type { Moment } from "moment"
import { AvailabilityTypes } from "@/enums/models/availability-types.ts"
import { AvailabilityBlock } from "@/models/availability-block.model.ts"
import type { AvailabilityBucket } from "@/models/availability-bucket.model.ts"
import {
  AvailabilityHelper,
  type UnavailabilityBlock
} from "@/helper/availability/availability-helper.ts"
import type { RecurringAvailability } from "@/models/recurring-availability.model.ts"
import type { KnownBusinessEntityContact } from "@/models/known-business-entity-contact.model.ts"

// ============================================================================
// CLASS DEFINITION
// ============================================================================

/**
 * Service-provider-specific availability helper.
 * Extends AvailabilityHelper with the "base availability" (contract-level AVAILABLE block)
 * concept.
 */
export class KnownBusinessEntityContactAvailabilityHelper extends AvailabilityHelper {
  // ============================================================================
  // BASE AVAILABILITY
  // ============================================================================

  /**
   * Finds the single contract-level "available" block for a bucket, if one exists.
   */
  static findBaseAvailabilityBlock(bucket: AvailabilityBucket): AvailabilityBlock | null {
    return (
      bucket.availabilityBlocks.find(
        (block) => block.availabilityType === AvailabilityTypes.AVAILABLE
      ) ?? null
    )
  }

  /**
   * Checks whether the given date falls within the bucket's contracted availability window.
   * Returns false if no date is provided; returns false when no contract block is configured
   * or when the block has neither a start nor an end time.
   */
  static isWithinBaseAvailability(
    bucket: AvailabilityBucket,
    date: Moment | null | undefined
  ): boolean {
    if (!date) {
      return false
    }

    const block = this.findBaseAvailabilityBlock(bucket)

    if (!block || (!block.timeStart && !block.timeEnd)) {
      return false
    }

    return this.isMomentWithinAvailabilityBlock(block.timeStart, block.timeEnd, date)
  }

  /**
   * Computes the base-availability unavailability blocks for a bucket within the given range.
   * These represent the time outside the contract-level AVAILABLE block bounds.
   * When the contract block has neither a start nor an end time, the entire range is unavailable.
   */
  static computeBaseAvailabilityUnavailabilityBlocks(
    bucket: AvailabilityBucket,
    rangeStart: Moment,
    rangeEnd: Moment
  ): UnavailabilityBlock[] {
    const contractBlock = this.findBaseAvailabilityBlock(bucket)

    if (!contractBlock) {
      return [] // This should never happen as someone should always have a contract block, but we return an empty array to avoid errors.
    }

    if (!contractBlock.timeStart && !contractBlock.timeEnd) {
      return [
        {
          source: contractBlock,
          timeStart: rangeStart.clone(),
          timeEnd: rangeEnd.clone()
        }
      ]
    }

    const blocks: UnavailabilityBlock[] = []

    // Before the contract start → unavailable
    if (contractBlock.timeStart) {
      const effectiveStart = this.adjustForMidnight(contractBlock.timeStart, false)!
      if (effectiveStart.isAfter(rangeStart)) {
        blocks.push({
          source: contractBlock,
          timeStart: rangeStart.clone(),
          timeEnd: effectiveStart.clone()
        })
      }
    }

    // After the contract end → unavailable
    if (contractBlock.timeEnd) {
      const effectiveEnd = this.adjustForMidnight(contractBlock.timeEnd, true)!
      if (effectiveEnd.isBefore(rangeEnd)) {
        blocks.push({
          source: contractBlock,
          timeStart: effectiveEnd.clone(),
          timeEnd: rangeEnd.clone()
        })
      }
    }

    return blocks
  }

  // ============================================================================
  // FORMATTING (override)
  // ============================================================================

  /**
   * Overrides the parent to include base availability description for AVAILABLE blocks.
   */
  static override formatUnavailabilitySource(
    source: AvailabilityBlock | RecurringAvailability,
    date?: Moment
  ): string {
    if (
      source instanceof AvailabilityBlock &&
      source.availabilityType === AvailabilityTypes.AVAILABLE
    ) {
      if (!source.timeStart && !source.timeEnd) {
        return "Er is geen basisbeschikbaarheid gedefiniëerd"
      }

      const fmt = (time: Moment | null | undefined) =>
        time?.format(
          time.hour() === 0 && time.minute() === 0 ? "DD-MM-YYYY" : "DD-MM-YYYY HH:mm [uur]"
        ) ?? "?"

      if (!source.timeEnd) {
        return `Basisbeschikbaarheid begint op ${fmt(source.timeStart)}`
      }

      if (!source.timeStart) {
        return `Basisbeschikbaarheid eindigt op ${fmt(source.timeEnd)}`
      }

      return `Basisbeschikbaarheid loopt van ${fmt(source.timeStart)} tot en met ${fmt(source.timeEnd)}`
    }
    return AvailabilityHelper.formatUnavailabilitySource(source, date)
  }

  // ============================================================================
  // CONVENIENCE QUERIES
  // ============================================================================

  /**
   * Returns whether a known business entity contact is available at the exact given moment.
   * Checks base availability first, then delegates to the parent for recurring availability
   * and unavailability block checks.
   */
  static isAvailableAtMoment(bucket: AvailabilityBucket, moment: Moment): boolean {
    return (
      this.isWithinBaseAvailability(bucket, moment) &&
      AvailabilityHelper.isAvailableAtMoment(bucket, moment)
    )
  }

  // ============================================================================
  // UNAVAILABILITY COMPUTATION (override)
  // ============================================================================

  /**
   * Extends the parent by appending base-availability blocks.
   * The returned blocks may overlap and each has a single source.
   */
  static override computeUnavailabilityBlocks(
    bucket: AvailabilityBucket,
    rangeStart: Moment,
    rangeEnd: Moment
  ): UnavailabilityBlock[] {
    return [
      ...AvailabilityHelper.computeUnavailabilityBlocks(bucket, rangeStart, rangeEnd),
      ...this.computeBaseAvailabilityUnavailabilityBlocks(bucket, rangeStart, rangeEnd)
    ]
  }

  // ============================================================================
  // FILTERING
  // ============================================================================

  /**
   * Returns KBECs that are within base availability but are (partially or fully) unavailable on the given date.
   */
  static filterUnavailableServiceProviders(
    kbecs: KnownBusinessEntityContact[],
    date: Moment
  ): KnownBusinessEntityContact[] {
    return kbecs.filter(
      (serviceProvider) =>
        // Ensure the KBEC is within its base availability bounds
        this.isWithinBaseAvailability(serviceProvider.availabilityBucket, date) &&
        // Use the specific KBEC helper for partial availability (will also take into account partial base availability)
        (this.isPartiallyAvailableOnDate(serviceProvider.availabilityBucket, date) ||
          // Use the base helper for full-day unavailability (will not regard full-day base unavailability)
          AvailabilityHelper.isEntireDayUnavailableOnDate(serviceProvider.availabilityBucket, date))
    )
  }

  /**
   * Returns KBECs that are within base availability and are not entirely unavailable on the given date.
   */
  static filterAvailableServiceProviders(
    kbecs: KnownBusinessEntityContact[],
    date: Moment
  ): KnownBusinessEntityContact[] {
    return kbecs.filter(
      (serviceProvider) =>
        // Ensure the KBEC is within its base availability bounds
        this.isWithinBaseAvailability(serviceProvider.availabilityBucket, date) &&
        !AvailabilityHelper.isEntireDayUnavailableOnDate(serviceProvider.availabilityBucket, date)
    )
  }
}
